import {
  call, put, select, all, takeEvery,
} from 'redux-saga/effects';
import { request } from 'utils/request';
import { IRestApiResponse } from 'interfaces';
import { selectEnterprise } from 'base/core/selectors';
import { CoreSliceActions } from 'base/core/slice';
import { selectExperts } from 'pages/groups/edit-group/selector';
import { IExpert } from 'pages/profile/interface';
import { selectAuthUser } from 'base/auth/selectors';
import { currencyFormat } from 'utils/formats';
import { CategoryResourceType } from 'constants/sessions';
import { articleActions } from './slice';
import API from './constants';

import {
  IArticleByExpertsFilterAction,
  IArticleFilterAction,
  ICreateArticleStateAction,
  IDeleteArticleAction,
  IPreviewArticleAction,
  IPublishArticleAction,
  IUpdateArticleAction,
} from './interface';

export function* searchArticleGenerator({ payload }: IArticleFilterAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_ARTICLES_BY_AUTHOR.path}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&sortBy=${payload.sortBy}`,
        method: API.GET_ARTICLES_BY_AUTHOR.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(
        articleActions.searchArticleSucceeded({
          articles: response.data.result,
          total: response.data.total,
        }),
      );
    } else {
      yield put(articleActions.searchArticleFailed());
    }
  } catch (error) {
    yield put(articleActions.searchArticleFailed());
  }
}

export function* getExpertsGenerator(): any {
  try {
    const enterprise = yield select(selectEnterprise);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_EXPERTS_LIST.path}/${enterprise.id}`,
        method: API.GET_EXPERTS_LIST.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(articleActions.getExpertsSucceeded(response.data));
    } else {
      yield put(articleActions.getExpertsFailed());
    }
  } catch (error) {
    yield put(articleActions.getExpertsFailed());
  }
}

export function* fetchArticlesGenerator({ payload }: IArticleByExpertsFilterAction): any {
  try {
    const expertIds = ((yield select(selectExperts)) as IExpert[]).map((expert) => expert.id);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_ARTICLES_BY_ENT.path}/?experts=${[...expertIds]}&keyword=${
          payload.keyword
        }&page=${payload.page}&limit=${payload.limit}&sortBy=${payload.sortBy}`,
        method: API.GET_ARTICLES_BY_ENT.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(
        articleActions.fetchArticlesSucceeded({
          articles: response.data.result,
          total: response.data.total,
        }),
      );
    } else {
      yield put(articleActions.fetchArticlesFailed());
    }
  } catch (error) {
    yield put(articleActions.fetchArticlesFailed());
  }
}

export function* deleteArticleGenerator({ payload }: IDeleteArticleAction): any {
  try {
    const id = payload;
    const response: IRestApiResponse = yield call(request, API.DELETE_ARTICLE, { id }, true);
    if (response.statusCode === 200) {
      yield put(articleActions.deleteArticleSucceeded(payload));
      // yield put(articleActions.closeModal(payload));
    } else {
      yield put(articleActions.deleteArticleFailed());
    }
  } catch (error) {
    yield put(articleActions.deleteArticleFailed());
  }
}

function* getCategories(resourceId: string) {
  const response: IRestApiResponse = yield call(
    request,
    {
      path: `${API.GET_CATEGORIES.path}?resourceId=${resourceId}&resourceType=${CategoryResourceType.ARTICLE}`,
      method: API.GET_CATEGORIES.method,
    },
    null,
    true,
  );

  return response;
}

export function* previewArticleGenerator({ payload }: IPreviewArticleAction): any {
  try {
    const id = payload;
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_ARTICLES_SUMMARY.path}/${id}`,
        method: API.GET_ARTICLES_SUMMARY.method,
      },
      null,
      true,
    );

    if (response.statusCode === 200) {
      const categories: IRestApiResponse = yield getCategories(id);
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';

      const formattedSessions = yield all(response.data.products.map(async (session: any) => {
        const fee = await currencyFormat(session.fee, session.currency ?? 'AUD', preferredCurrency);
        const subscriberFee = await currencyFormat(session.subscriberFee, session.subscriberCurrency ?? 'AUD', preferredCurrency);
        const observerFee = await currencyFormat(session.observerFee, session.observerCurrency ?? 'AUD', preferredCurrency);
        const subscribeObserverFee = await currencyFormat(session.subscribeObserverFee, session.subscribeObserverCurrency ?? 'AUD', preferredCurrency);
        return {
          ...session,
          fee,
          subscriberFee,
          observerFee,
          subscribeObserverFee,
        };
      }));
      yield put(articleActions.previewArticleSucceeded({
        ...response.data,
        products: formattedSessions,
        categories: categories.data,
      }));
    } else {
      yield put(articleActions.previewArticleFailed());
    }
  } catch (error) {
    console.log(error);
    yield put(articleActions.previewArticleFailed());
  }
}

export function* editArticleFetchGenerator({ payload }: IPreviewArticleAction): any {
  try {
    const id = payload;
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_ARTICLES_SUMMARY.path}/${id}`,
        method: API.GET_ARTICLES_SUMMARY.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(articleActions.previewArticleSucceeded(response.data));
    } else {
      yield put(articleActions.previewArticleFailed());
    }
  } catch (error) {
    console.log(error);
    yield put(articleActions.previewArticleFailed());
  }
}

export function* publishArticleGenerator({ payload }: IPublishArticleAction): any {
  try {
    const { id, isActive } = payload;
    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_ARTICLE,
      { id, isActive },
      true,
    );
    if (response.statusCode === 201) {
      yield put(articleActions.publishArticleSucceeded());
      // yield put(articleActions.closeModal(payload.id));
    } else {
      yield put(articleActions.publishArticleFailed());
    }
  } catch (error) {
    yield put(articleActions.publishArticleFailed());
  }
}

function* assignCategories(resourceId: string, categories: string[]) {
  yield call(
    request,
    API.POST_ASSIGN_CATEGORIES,
    {
      resourceId,
      categoryId: categories ?? [],
      resourceType: CategoryResourceType.ARTICLE,
    },
  );
}

export function* updateArticleGenerator({ payload }: IUpdateArticleAction): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      API.POST_UPDATE_ARTICLE,
      { id: payload.id, ...payload.data },
      true,
    );
    const { categories } = payload.data;
    if (response.statusCode === 201) {
      yield assignCategories(payload.id, categories);
      yield put(articleActions.updateArticleSucceeded());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Your article has been updated successfully.',
          btnText: 'Go to articles',
        }),
      );
    } else {
      yield put(articleActions.updateArticleFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error updating your article.',
          btnText: 'Try updating again',
        }),
      );
    }
  } catch (error) {
    yield put(articleActions.updateArticleFailed());
    yield put(
      CoreSliceActions.displayAlert({
        type: 'error',
        message: 'There was an error updating your article.',
        btnText: 'Try updating again',
      }),
    );
  }
}

export function* createArticleGenerator({ payload }: ICreateArticleStateAction): any {
  try {
    const { id: entId } = yield select(selectEnterprise);
    const response: IRestApiResponse = yield call(
      request,
      API.POST_CREATE_ARTICLE,
      {
        entId,
        isActive: true,
        ...payload,
      },
      true,
    );
    const { categories } = payload;
    if (response.statusCode === 201) {
      yield assignCategories(response.data?.createArticle?.id, categories);
      yield put(articleActions.createArticleSucceeded());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Your article has been published successfully.',
          btnText: 'Go to articles',
        }),
      );
    } else {
      yield put(articleActions.createArticleFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error publishing your article.',
          btnText: 'Try publishing again',
        }),
      );
    }
  } catch (error) {
    yield put(articleActions.createArticleFailed());
  }
}

export function* articleSaga() {
  yield all([
    takeEvery(articleActions.createArticle.type, createArticleGenerator),
    takeEvery(articleActions.searchArticle.type, searchArticleGenerator),
    takeEvery(articleActions.previewArticle.type, previewArticleGenerator),
    takeEvery(articleActions.publishArticle.type, publishArticleGenerator),
    takeEvery(articleActions.updateArticle.type, updateArticleGenerator),
    takeEvery(articleActions.deleteArticle.type, deleteArticleGenerator),
    takeEvery(articleActions.getExperts.type, getExpertsGenerator),
    takeEvery(articleActions.fetchArticles.type, fetchArticlesGenerator),
  ]);
}

export default articleSaga;
